/**
 * `useContourPreview` — exponential-backoff polling hook for the pre-order
 * server-side contour preview pipeline (PL-CONTOUR-101 / SF-CONTOUR-105).
 *
 * Polls with a manual setTimeout backoff, refs for mutable non-rendering
 * state and a stable imperative handle, rather than a TanStack Query poll.
 *
 * Accepts a nullable artwork file id. On a non-null id it queues a preview
 * exactly once (`POST /contour/previews`) and polls
 * `GET /contour/previews/:id` on backoff starting at 1s, capped at 4s —
 * previews are interactive feedback and settle in a few seconds, not the
 * minutes an order-finalize job can take. `succeeded` / `failed` are
 * terminal and stop polling. `retry()` re-queues a `failed` preview
 * (`POST /contour/previews/:id/retry`) and resumes polling the same id.
 *
 * SF-CONTOUR-106 removed the in-browser background-removal/contour-detection
 * pipeline entirely — this is now the only preview path, driving every file
 * regardless of size (see `AutoContourArtworkFlowContainer`).
 *
 * `backgroundRemovalMode` is part of the identity: changing it re-queues
 * (the server dedupes on artwork + mode + owner) and resets the state.
 *
 * SF-CONTOUR-105, SF-CONTOUR-106, SF-CONTOUR-110.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

import { createContourPreview, getContourPreview, retryContourPreview } from '@dfq/shared/api/contourPreview';
import type {
  ContourBackgroundRemovalMode,
  ContourPreviewStatus,
  RawContourPathJson,
} from '@dfq/shared/types/contourPreview';

const INITIAL_DELAY_MS = 1_000;
const MAX_DELAY_MS = 4_000;

export interface UseContourPreviewResult {
  /** Server preview id once queued for the current artwork + mode. */
  previewId: number | null;
  rawContourPathJson: RawContourPathJson | null;
  originalWidthPx: number | null;
  originalHeightPx: number | null;
  targetDpi: number | null;
  /** `idle` until a preview has been queued for the current artwork file id. */
  status: ContourPreviewStatus | 'idle';
  previewImageUrl: string | null;
  cutPathUrl: string | null;
  widthInches: number | null;
  heightInches: number | null;
  /** Message attached to a `failed` preview. */
  errorMessage: string | null;
  /** Set when the queue/poll/retry call itself fails (network/envelope error) — distinct from a `failed` preview. */
  queueError: string | null;
  retryable: boolean;
  /** Re-queue a `failed` preview. No-op before a preview has been queued. */
  retry: () => void;
  isRetrying: boolean;
}

export function useContourPreview(
  artworkFileId: number | null,
  backgroundRemovalMode?: ContourBackgroundRemovalMode,
): UseContourPreviewResult {
  const [previewId, setPreviewId] = useState<number | null>(null);
  const [rawContourPathJson, setRawContourPathJson] = useState<RawContourPathJson | null>(null);
  const [originalWidthPx, setOriginalWidthPx] = useState<number | null>(null);
  const [originalHeightPx, setOriginalHeightPx] = useState<number | null>(null);
  const [targetDpi, setTargetDpi] = useState<number | null>(null);
  const [status, setStatus] = useState<ContourPreviewStatus | 'idle'>('idle');
  const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null);
  const [cutPathUrl, setCutPathUrl] = useState<string | null>(null);
  const [widthInches, setWidthInches] = useState<number | null>(null);
  const [heightInches, setHeightInches] = useState<number | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [queueError, setQueueError] = useState<string | null>(null);
  const [retryable, setRetryable] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);

  const previewIdRef = useRef<number | null>(null);
  const runRef = useRef(0);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const delayRef = useRef<number>(INITIAL_DELAY_MS);
  const hasStopped = useRef(false);
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const clearScheduled = useCallback((): void => {
    if (timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const applyPreview = useCallback((preview: Awaited<ReturnType<typeof getContourPreview>>): void => {
    if (!isMounted.current) return;
    setStatus(preview.status);
    setPreviewImageUrl(preview.previewImageUrl);
    setCutPathUrl(preview.cutPathUrl);
    setWidthInches(preview.widthInches);
    setHeightInches(preview.heightInches);
    setErrorMessage(preview.errorMessage);
    setRetryable(preview.retryable);
    setPreviewId(preview.id);
    setRawContourPathJson(preview.rawContourPathJson);
    setOriginalWidthPx(preview.originalWidthPx);
    setOriginalHeightPx(preview.originalHeightPx);
    setTargetDpi(preview.targetDpi);
  }, []);

  const poll = useCallback(
    async (previewId: number): Promise<void> => {
      if (hasStopped.current) return;

      let preview;
      try {
        preview = await getContourPreview(previewId);
      } catch {
        // Transient network/server error — keep polling on backoff.
        if (!hasStopped.current && isMounted.current) scheduleNext(previewId);
        return;
      }

      if (hasStopped.current || !isMounted.current || previewIdRef.current !== previewId) return;
      applyPreview(preview);

      if (preview.status === 'succeeded' || preview.status === 'failed') {
        hasStopped.current = true;
        clearScheduled();
        return;
      }

      scheduleNext(previewId);
    },
    // scheduleNext is declared below (module-scope function, not a hook
    // value) to avoid a circular useCallback dependency with pollRef.
    [applyPreview, clearScheduled],
  );

  const pollRef = useRef(poll);
  pollRef.current = poll;

  function scheduleNext(previewId: number): void {
    if (hasStopped.current) return;
    const delay = delayRef.current;
    delayRef.current = Math.min(delay * 2, MAX_DELAY_MS);
    timeoutRef.current = setTimeout(() => {
      void pollRef.current(previewId);
    }, delay);
  }

  // Reset to idle when artworkFileId transitions to null, and clear any
  // stale queue error on every transition (including one non-null id to
  // another) — setState during render, not an effect, so callers see the
  // reset synchronously and the effect below never has to touch state
  // before its own `await`. See
  // https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes
  const [prevKey, setPrevKey] = useState(`${artworkFileId}:${backgroundRemovalMode}`);
  const key = `${artworkFileId}:${backgroundRemovalMode}`;
  if (key !== prevKey) {
    setPrevKey(key);
    setQueueError(null);
    setStatus('idle');
    setPreviewId(null);
    setPreviewImageUrl(null);
    setCutPathUrl(null);
    setWidthInches(null);
    setHeightInches(null);
    setErrorMessage(null);
    setRetryable(false);
    setRawContourPathJson(null);
    setOriginalWidthPx(null);
    setOriginalHeightPx(null);
    setTargetDpi(null);
  }

  useEffect(() => {
    if (artworkFileId === null) {
      hasStopped.current = true;
      clearScheduled();
      previewIdRef.current = null;
      runRef.current += 1;
      return;
    }

    hasStopped.current = false;
    delayRef.current = INITIAL_DELAY_MS;
    previewIdRef.current = null;
    const run = ++runRef.current;

    const start = async (): Promise<void> => {
      let created;
      try {
        created = await createContourPreview(artworkFileId, backgroundRemovalMode);
      } catch (err) {
        if (!isMounted.current || runRef.current !== run) return;
        setQueueError(err instanceof Error ? err.message : 'Failed to queue artwork preview');
        return;
      }
      if (hasStopped.current || !isMounted.current || runRef.current !== run) return;
      previewIdRef.current = created.id;
      applyPreview(created);
      if (created.status !== 'succeeded' && created.status !== 'failed') {
        scheduleNext(created.id);
      }
    };

    void start();

    return () => {
      hasStopped.current = true;
      clearScheduled();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [artworkFileId, backgroundRemovalMode]);

  const retry = useCallback((): void => {
    const previewId = previewIdRef.current;
    if (previewId === null || isRetrying) return;

    const run = runRef.current;
    const isStale = (): boolean => !isMounted.current || runRef.current !== run;

    setIsRetrying(true);
    setQueueError(null);
    retryContourPreview(previewId)
      .then((updated) => {
        if (isStale()) return;
        applyPreview(updated);
        hasStopped.current = false;
        delayRef.current = INITIAL_DELAY_MS;
        scheduleNext(previewId);
      })
      .catch((err: unknown) => {
        if (isStale()) return;
        setQueueError(err instanceof Error ? err.message : 'Failed to retry artwork preview');
      })
      .finally(() => {
        if (isMounted.current) setIsRetrying(false);
      });
  }, [applyPreview, isRetrying]);

  return {
    previewId,
    rawContourPathJson,
    originalWidthPx,
    originalHeightPx,
    targetDpi,
    status,
    previewImageUrl,
    cutPathUrl,
    widthInches,
    heightInches,
    errorMessage,
    queueError,
    retryable,
    retry,
    isRetrying,
  };
}
